// École Nid Douillet - Sidebar Navigation Component
// Main navigation sidebar for authenticated users

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@store/authStore';
import { clsx } from 'clsx';
import {
  HomeIcon,
  UserGroupIcon,
  UsersIcon,
  CreditCardIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UserIcon,
  XMarkIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation(['common', 'auth']);
  const { user, isDirector, isParent } = useAuth();
  const location = useLocation();

  // Navigation items based on user role
  const navigationItems = [
    {
      name: t('navigation.dashboard'),
      href: '/dashboard',
      icon: HomeIcon,
      roles: ['DIRECTOR', 'PARENT'],
    },
    {
      name: t('navigation.children'),
      href: '/children',
      icon: UserGroupIcon,
      roles: ['DIRECTOR'],
    },
    {
      name: t('navigation.parents'),
      href: '/parents',
      icon: UsersIcon,
      roles: ['DIRECTOR'],
    },
    {
      name: t('navigation.payments'),
      href: '/payments',
      icon: CreditCardIcon,
      roles: ['DIRECTOR', 'PARENT'],
    },
    {
      name: t('navigation.analytics'),
      href: '/analytics',
      icon: ChartBarIcon,
      roles: ['DIRECTOR'],
    },
    {
      name: 'Site Web',
      href: '/cms',
      icon: GlobeAltIcon,
      roles: ['DIRECTOR'],
    },
    {
      name: t('navigation.settings'),
      href: '/settings',
      icon: Cog6ToothIcon,
      roles: ['DIRECTOR'],
    },
    {
      name: t('navigation.profile'),
      href: '/profile',
      icon: UserIcon,
      roles: ['DIRECTOR', 'PARENT'],
    },
  ];

  // Filter navigation items based on user role
  const filteredItems = navigationItems.filter(item =>
    item.roles.includes(user?.role || '')
  );

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 transform bg-white shadow-lg transition-transform duration-300 ease-in-out lg:static lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Sidebar header */}
        <div className="flex h-16 items-center justify-between px-6 border-b border-neutral-200">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 rounded-lg bg-primary-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">ND</span>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-neutral-900">
                École Nid Douillet
              </h1>
            </div>
          </div>
          
          {/* Close button for mobile */}
          <button
            onClick={onClose}
            className="lg:hidden rounded-md p-2 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* User info */}
        <div className="p-6 border-b border-neutral-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-primary-700 font-medium text-sm">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-900 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-neutral-500 truncate">
                {t(`auth:roles.${user?.role}`)}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1">
          {filteredItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onClose}
                className={clsx(
                  'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  active
                    ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                    : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900'
                )}
              >
                <Icon
                  className={clsx(
                    'mr-3 h-5 w-5 flex-shrink-0',
                    active
                      ? 'text-primary-600'
                      : 'text-neutral-400 group-hover:text-neutral-600'
                  )}
                />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-neutral-200">
          <div className="text-xs text-neutral-500 text-center">
            <p>École Maternelle Nid Douillet</p>
            <p>Version 1.0.0</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
